import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { defaultPaginationState, PaginationState } from '@shared/models/pagination-state';
import { Group } from '@shared/models/group';
import { LoadingState } from '@shared/types/loading-state';
import { clearGroups, loadGroups, loadGroupsFailure, loadGroupsSuccess } from '../actions/load-groups.actions';
import {
  loadGroupDuplicates,
  loadGroupDuplicatesFailure,
  loadGroupDuplicatesSuccess,
} from '../actions/load-group-duplicates.actions';
import { GroupFilters } from '@shared/models/group-filters';
import { Sorting } from '@shared/models/sorting';
import { loadGroup, loadGroupSuccess } from '@shared/actions/load-group.actions';
import { selectGroup, selectGroupId } from '@shared/actions/select-group.actions';
import {
  sendAgreementForSignatures,
  sendAgreementForSignaturesFailure,
  sendAgreementForSignaturesSuccess,
} from '@shared/actions/send-agreement-for-signatures.actions';

export const featureKey = 'groups';

export const groupsAdapter = createEntityAdapter<Group>({
  selectId: (group: Group) => group.attributes.id,
});

export interface State extends EntityState<Group> {
  paginationState: PaginationState;
  filters: GroupFilters;
  sorting?: Sorting;
  loadingState: LoadingState;
  selectedGroupId: string | undefined;
}

export const initialState: State = groupsAdapter.getInitialState({
  paginationState: defaultPaginationState,
  filters: {},
  loadingState: 'not_loading',
  selectedGroupId: undefined,
});

export const groupsReducer = createReducer(
  initialState,
  on(loadGroups, loadGroup, sendAgreementForSignatures, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
  })),
  on(loadGroupDuplicates, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
  })),
  on(clearGroups, (state): State => {
    return groupsAdapter.removeAll({
      ...state,
      loadingState: 'not_loading',
    });
  }),
  on(loadGroupsSuccess, (state, { data, paginationState, filters, sorting }): State => {
    return groupsAdapter.setAll(data, {
      ...state,
      paginationState,
      loadingState: 'loaded' as LoadingState,
      filters: filters,
      sorting: sorting,
    });
  }),
  on(loadGroupDuplicatesSuccess, (state, { data, paginationState, filters, sorting }): State => {
    return groupsAdapter.setAll(data, {
      ...state,
      paginationState,
      loadingState: 'loaded' as LoadingState,
      filters: filters,
      sorting: sorting,
    });
  }),
  on(loadGroupSuccess, (state, { group }): State => {
    return groupsAdapter.setOne(group, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      selectedGroupId: group.attributes.id,
    });
  }),
  on(sendAgreementForSignaturesSuccess, (state) => ({
    ...state,
    loadingState: 'loaded' as LoadingState,
  })),
  on(selectGroup, (state, { group }) => ({
    ...state,
    selectedGroupId: group.attributes.id,
  })),
  on(selectGroupId, (state, { groupId }) => ({
    ...state,
    selectedGroupId: groupId,
  })),
  on(loadGroupsFailure, sendAgreementForSignaturesFailure, (state) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
  })),
  on(loadGroupDuplicatesFailure, (state) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return groupsReducer(state, action);
}

export const groupsFeatureSelector = createFeatureSelector<State>(featureKey);

const { selectIds, selectEntities, selectAll, selectTotal } = groupsAdapter.getSelectors(groupsFeatureSelector);

export const selectAllGroups = selectAll;

export const selectGroupsLoadingState = createSelector(groupsFeatureSelector, (state: State) => state.loadingState);

export const selectGroupsPaginationState = createSelector(
  groupsFeatureSelector,
  (state: State) => state.paginationState,
);

export const selectSelectedGroupId = createSelector(groupsFeatureSelector, (state: State) => state.selectedGroupId);

export const selectSelectedGroup = createSelector(selectEntities, selectSelectedGroupId, (entities, groupId) =>
  groupId == undefined ? undefined : entities[groupId],
);
export const selectGroupById = (groupId: string) =>
  createSelector(selectEntities, (entities) => (groupId == undefined ? undefined : entities[groupId]));
